import React from 'react';
import { motion } from 'framer-motion';
import HandwrittenChecklist from './HandwrittenChecklist';
import StickyNote from './StickyNote';
import ScribbleUnderline from './ScribbleUnderline';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const workLog = [
  { time: 'Monday morning', items: [
    { text: 'Moved dentist appt → Tuesday 2PM', done: true },
    { text: 'Drafted reply to landlord re: boiler', done: true },
    { text: 'Reminder set: call Mum Sunday 6PM', done: true },
  ]},
  { time: 'Tuesday afternoon', items: [
    { text: 'Found flights to Lisbon from £189', done: true },
    { text: 'Booked restaurant for Friday — confirmed', done: true },
    { text: 'Grocery list sent to phone', done: true },
  ]},
  { time: 'Wednesday', items: [
    { text: 'Birthday reminder: Jess — Saturday', done: true },
    { text: 'Gift idea researched + link sent', done: true },
    { text: 'Car service booked for next week', done: true },
  ]},
  { time: 'Thursday', items: [
    { text: 'Weekly summary sent at 8AM', done: true },
    { text: 'Hotel options for Lisbon shortlisted', done: true },
    { text: 'Catch-up with Tom — rescheduled', done: false },
  ]},
];

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
};

const NotebookWorkLog = () => (
  <section className="py-24 relative overflow-hidden">
    <div className="absolute inset-0 notebook-bg" />
    <div className="absolute top-0 bottom-0 left-[88px] w-px bg-[rgba(220,80,80,0.16)] hidden lg:block" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-4 lg:pl-16">
          <div className="tag-pill mb-5">Claw's week log</div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A1A18] mb-3 leading-tight tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            What Claw did<br />
            <span className="gradient-text-accent">while you lived your life.</span>
          </h2>
          <ScribbleUnderline color="rgba(45,106,143,0.22)" width={200} />
          <p className="text-[#6B6560] text-sm leading-relaxed mt-4 mb-8">
            A real snapshot of the kind of tasks Claw handles every week. No prompts needed — just tell Claw once, and it remembers.
          </p>
          <div className="space-y-6 pl-4 border-l-2 border-[rgba(45,106,143,0.14)]">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <p className="font-handwritten text-lg text-[#2D6A8F]" style={{ transform: 'rotate(-1deg)' }}>
                ← all done without being asked twice
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 }}>
              <p className="font-handwritten text-base text-[#3D7A5E]" style={{ transform: 'rotate(1.5deg)' }}>
                saved ~6 hrs this week
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.6 }}>
              <p className="font-handwritten text-base text-[#C17B4A]" style={{ transform: 'rotate(-1deg)' }}>
                * Tom rescheduled — still working on it
              </p>
            </motion.div>
          </div>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
          {workLog.map((block, i) => (
            <motion.div key={block.time} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
              className="bg-white border border-[#E8E2DA] rounded-xl p-5 relative overflow-hidden shadow-sm">
              <div className="absolute inset-0 ruled-lines opacity-40 pointer-events-none rounded-xl" />
              <div className="relative">
                <div className="font-mono-custom text-[10px] text-[#9E9690] uppercase tracking-widest mb-3">{block.time}</div>
                <HandwrittenChecklist items={block.items} delay={i * 0.1} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="mt-16 flex flex-wrap justify-center gap-8 items-start">
        <StickyNote color="yellow" rotate="-2deg" delay={0.1}>
          <p className="text-sm font-bold mb-1">This week's wins</p>
          <p className="text-xs">🗓 12 things scheduled</p>
          <p className="text-xs">💬 8 messages drafted</p>
          <p className="text-xs">⏱ ~6 hrs saved</p>
          <p className="text-xs text-[#9E9690] mt-1 font-semibold">— Claw</p>
        </StickyNote>
        <StickyNote color="green" rotate="1.5deg" delay={0.2}>
          <p className="text-sm font-bold mb-1">Tone matched ✓</p>
          <p className="text-xs">Replies now sound</p>
          <p className="text-xs">exactly like you</p>
          <p className="text-xs text-[#14532D] mt-1">Day 7 milestone</p>
        </StickyNote>
        <StickyNote color="blue" rotate="-1deg" delay={0.3}>
          <p className="text-sm font-bold mb-1">Lisbon trip 🌊</p>
          <p className="text-xs">3 hotels shortlisted</p>
          <p className="text-xs">Flights from £189</p>
          <p className="text-xs text-[#1E3A5F] mt-1">links in your email</p>
        </StickyNote>
        <StickyNote color="pink" rotate="2.5deg" delay={0.4}>
          <p className="text-sm font-bold mb-1">Don't forget 🎂</p>
          <p className="text-xs">Jess's birthday</p>
          <p className="text-xs">Saturday!</p>
          <p className="text-xs text-[#881337] mt-1">gift idea sent</p>
        </StickyNote>
      </div>

      {/* Soft CTA */}
      <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        transition={{ delay: 0.3 }} className="mt-12 text-center">
        <button onClick={() => scrollToSection('quiz')}
          className="inline-flex items-center gap-2 text-[#2D6A8F] font-semibold text-sm hover:text-[#1E4D7A] transition-colors group">
          What could Claw do for you?
          <SafeIcon icon={FiIcons.FiArrowRight} className="text-xs group-hover:translate-x-0.5 transition-transform" />
        </button>
      </motion.div>
    </div>
  </section>
);

export default NotebookWorkLog;